"use client";

import React, { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useRouter } from "next/navigation";
import { MemberAvatar } from "@/components/tailwind/ui/memberAvatar";
import { useWorkspaceContext } from "@/contexts/workspaceContext";

interface MentionHoverCardProps {
  userId: string;
  userName: string;
  userEmail?: string;
  /** Extra classes for the inline trigger wrapper (e.g. flex-shrink-0). */
  className?: string;
  /** Optional line above the profile, e.g. "Last viewed 4 months ago". */
  heading?: React.ReactNode;
  /** Off where the card is purely an identity peek (e.g. visitor badges). */
  showEmail?: boolean;
  children: React.ReactNode;
}

export const MentionHoverCard: React.FC<MentionHoverCardProps> = ({
  userId,
  userName,
  userEmail,
  className,
  heading,
  showEmail = true,
  children,
}) => {
  const router = useRouter();
  const { getMemberName } = useWorkspaceContext();
  const displayName = getMemberName(userId) || getMemberName(userEmail) || userName;
  const [isHovered, setIsHovered] = useState(false);
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);
  const [position, setPosition] = useState({ x: 0, y: 0, opacity: 0 });
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = (e: React.MouseEvent) => {
    // Cancel any pending hide
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setTriggerRect(e.currentTarget.getBoundingClientRect());
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    // Small delay to allow moving to card
    timeoutRef.current = setTimeout(() => {
      setIsHovered(false);
      setTriggerRect(null);
      setPosition(prev => ({ ...prev, opacity: 0 }));
    }, 150);
  };

  const handleCardEnter = () => {
    // Cancel hide when entering card
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleCardLeave = () => {
    setIsHovered(false);
    setTriggerRect(null);
    setPosition(prev => ({ ...prev, opacity: 0 }));
  };

  React.useEffect(() => {
    if (isHovered && triggerRect && cardRef.current) {
      const cardRect = cardRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let x = triggerRect.left;
      let y = triggerRect.bottom + 8; // Default below

      // Right overflow
      if (x + cardRect.width > viewportWidth - 16) {
        x = viewportWidth - cardRect.width - 16;
      }
      
      // Left overflow
      if (x < 16) {
        x = 16;
      }

      // Bottom overflow
      if (y + cardRect.height > viewportHeight - 16) {
        // Place above the trigger
        y = triggerRect.top - cardRect.height - 8;
        
        // If placing above goes out of screen at top, keep it below
        if (y < 16) {
          y = triggerRect.bottom + 8;
        }
      }

      setPosition({ x, y, opacity: 1 });
    }
  }, [isHovered, triggerRect]);

  // Instantly hide the card if the user scrolls
  React.useEffect(() => {
    const handleScroll = () => {
      if (isHovered) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        setIsHovered(false);
        setTriggerRect(null);
        setPosition(prev => ({ ...prev, opacity: 0 }));
      }
    };

    if (isHovered) {
      window.addEventListener("scroll", handleScroll, { passive: true, capture: true });
    }

    return () => {
      window.removeEventListener("scroll", handleScroll, { capture: true });
    };
  }, [isHovered]);

  const currentTimestamp = new Date().toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });

  return (
    <>
      <span
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`relative inline-block ${className ?? ""}`}
      >
        {children}
      </span>

      {isHovered && typeof document !== 'undefined' && createPortal(
        <div
          ref={cardRef}
          className="fixed z-[100000] bg-background border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl max-w-[400px] min-w-[280px] p-3 cursor-pointer transition-opacity duration-150"
          style={{
            left: `${position.x}px`,
            top: `${position.y}px`,
            opacity: position.opacity,
          }}
          onMouseEnter={handleCardEnter}
          onMouseLeave={handleCardLeave}
          onClick={() => { if (userId) router.push(`/userprofile/${userId}`); }}
        >
          <div className="flex flex-col gap-4">
            {heading && (
              <div className="-mx-3 -mt-3 border-b border-gray-200 px-3 py-2 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
                {heading}
              </div>
            )}
            <div className="flex flex-row">
              <div className="flex items-center gap-3 overflow-hidden">
                {/* Avatar */}
                <MemberAvatar
                  name={displayName}
                  email={userEmail}
                  userId={userId}
                  className="flex-shrink-0 w-14 h-14 rounded-full select-none bg-background text-gray-600 dark:text-gray-400 text-2xl outline outline-1 outline-black/10 -outline-offset-1"
                />

                {/* User Info */}
                <div className="flex flex-col gap-[3px] overflow-hidden w-full">
                  {/* Name */}
                  <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap overflow-hidden text-ellipsis">
                    {displayName}
                  </div>

                  {/* Email */}
                  {showEmail && userEmail && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap overflow-hidden text-ellipsis">
                      {userEmail}
                    </div>
                  )}

                  {/* Local Time */}
                  <div className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap overflow-hidden text-ellipsis">
                    {currentTimestamp} local time
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};
